import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AppLayout from '../Layouts/AppLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Shield } from 'lucide-react';
import { rolesPermissionsService, Role, Permission } from '@/services/rolesPermissionsService';
import { useAlert } from '@/hooks/useAlert';
import { 
  SearchInput,
  RolesList,
  PermissionsList,
  PermissionFormModal,
  AssignPermissionsModal,
  AssignRolesModal,
  DeleteRoleModal
} from './RolesPermissions/index';

const RolesPermissions = () => {
  const queryClient = useQueryClient();
  const { successAlert, errorAlert } = useAlert();
  
  // Estado para la tab activa con persistencia en sessionStorage
  const [activeTab, setActiveTab] = useState<string>(() => {
    return sessionStorage.getItem('rolesPermissions_activeTab') || 'roles';
  });
  
  // Estados de modales
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isAssignRolesModalOpen, setIsAssignRolesModalOpen] = useState(false);
  const [isDeleteRoleModalOpen, setIsDeleteRoleModalOpen] = useState(false);
  
  // Estados de edición
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  
  // Estados de selección
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  
  // Estados de búsqueda
  const [permissionSearchText, setPermissionSearchText] = useState('');
  const [mainSearchText, setMainSearchText] = useState('');

  // Queries
  const { data: rolesBase = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: rolesPermissionsService.roles.getAll
  });

  const { data: rolesWithCounts = [] } = useQuery({
    queryKey: ['roles-user-counts'],
    queryFn: rolesPermissionsService.roles.getUserCounts
  });

  // Combinar los datos base con los conteos de usuarios
  const roles = rolesBase.map(role => {
    const roleWithCount = rolesWithCounts.find(r => r.id === role.id);
    return {
      ...role,
      users_count: roleWithCount?.users_count ?? 0
    };
  });

  const { data: permissions = [], isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: rolesPermissionsService.permissions.getAll
  });

  // Effects
  useEffect(() => {
    sessionStorage.setItem('rolesPermissions_activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    setMainSearchText('');
  }, [activeTab]);

  // Mutations
  const createRoleMutation = useMutation({
    mutationFn: rolesPermissionsService.roles.create,
    onSuccess: (newRole) => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles-user-counts'] });
      setIsRoleModalOpen(false);
      setEditingRole(null);
      successAlert({
        title: 'Rol creado exitosamente',
        message: `El rol "${newRole.display_name || newRole.name}" ha sido creado.`
      });
    },
    onError: (error) => {
      errorAlert({
        title: 'Error al crear rol',
        message: 'No se pudo crear el rol. Inténtalo nuevamente.',
        exception: error
      });
    }
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      rolesPermissionsService.roles.update(id, data),
    onSuccess: (updatedRole) => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles-user-counts'] });
      setIsRoleModalOpen(false);
      setEditingRole(null);
      successAlert({
        title: 'Rol actualizado exitosamente',
        message: `El rol "${updatedRole.display_name || updatedRole.name}" ha sido actualizado.`
      });
    },
    onError: (error) => {
      errorAlert({
        title: 'Error al actualizar rol',
        message: 'No se pudo actualizar el rol. Inténtalo nuevamente.',
        exception: error
      });
    }
  });

  const deleteRoleMutation = useMutation({
    mutationFn: rolesPermissionsService.roles.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles-user-counts'] });
      const deletedRoleName = roleToDelete?.display_name || roleToDelete?.name || 'el rol';
      setIsDeleteRoleModalOpen(false);
      setRoleToDelete(null);
      successAlert({
        title: 'Rol eliminado exitosamente',
        message: `${deletedRoleName} ha sido eliminado del sistema.`
      });
    },
    onError: (error) => {
      errorAlert({
        title: 'Error al eliminar rol',
        message: 'No se pudo eliminar el rol. Puede que tenga usuarios asignados.',
        exception: error
      });
    }
  });

  const syncPermissionsMutation = useMutation({
    mutationFn: ({ roleId, permissionIds }: { roleId: number; permissionIds: number[] }) => {
      const permissionNames = permissions
        .filter(p => permissionIds.includes(p.id))
        .map(p => p.name);
      return rolesPermissionsService.rolePermissions.sync(roleId, { permissions: permissionNames });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles-user-counts'] });
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      const roleName = selectedRole?.display_name || selectedRole?.name || 'el rol';
      const permissionCount = selectedPermissions.length;
      closeAssignModal();
      successAlert({
        title: 'Permisos sincronizados exitosamente',
        message: `Se han asignado ${permissionCount} permisos a ${roleName}.`
      });
    },
    onError: (error) => {
      errorAlert({
        title: 'Error al sincronizar permisos',
        message: 'No se pudieron sincronizar los permisos. Inténtalo nuevamente.',
        exception: error
      });
    }
  });

  const syncRolesMutation = useMutation({
    mutationFn: ({ permissionId, roleIds }: { permissionId: number; roleIds: number[] }) => {
      const roleNames = roles
        .filter(r => roleIds.includes(r.id))
        .map(r => r.name);
      return rolesPermissionsService.rolePermissions.syncRolesToPermission(permissionId, roleNames);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles-user-counts'] });
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      const permissionName = selectedPermission?.display_name || selectedPermission?.name || 'el permiso';
      const roleCount = selectedRoles.length;
      closeAssignRolesModal();
      successAlert({
        title: 'Roles sincronizados exitosamente',
        message: `Se han asignado ${roleCount} roles al permiso ${permissionName}.`
      });
    },
    onError: (error) => {
      errorAlert({
        title: 'Error al sincronizar roles',
        message: 'No se pudieron sincronizar los roles. Inténtalo nuevamente.',
        exception: error
      });
    }
  });

  const updatePermissionMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      rolesPermissionsService.permissions.update(id, data),
    onSuccess: (updatedPermission) => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      setIsPermissionModalOpen(false);
      setEditingPermission(null);
      successAlert({
        title: 'Permiso actualizado exitosamente',
        message: `El permiso "${updatedPermission.display_name || updatedPermission.name}" ha sido actualizado.`
      });
    },
    onError: (error) => {
      errorAlert({
        title: 'Error al actualizar permiso',
        message: 'No se pudo actualizar el permiso. Inténtalo nuevamente.',
        exception: error
      });
    }
  });

  // Handlers for closing modals
  const closeAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedRole(null);
    setSelectedPermissions([]);
    setPermissionSearchText('');
  };

  const closeAssignRolesModal = () => {
    setIsAssignRolesModalOpen(false);
    setSelectedPermission(null);
    setSelectedRoles([]);
  };

  // Form handlers
  const handleRoleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name') as string,
      display_name: formData.get('display_name') as string || undefined,
      description: formData.get('description') as string || undefined,
      color: formData.get('color') as string || undefined,
      sort_order: parseInt(formData.get('sort_order') as string) || 0
    };

    if (editingRole) {
      updateRoleMutation.mutate({ id: editingRole.id, data });
    } else {
      createRoleMutation.mutate(data);
    }
  };

  const handlePermissionSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingPermission) return;

    const formData = new FormData(e.currentTarget);
    const data = {
      display_name: formData.get('display_name') as string || undefined,
      description: formData.get('description') as string || undefined,
      category: formData.get('category') as string || undefined,
    };

    updatePermissionMutation.mutate({ id: editingPermission.id, data });
  };

  // Action handlers
  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setIsRoleModalOpen(true);
  };

  const handleEditPermission = (permission: Permission) => {
    setEditingPermission(permission);
    setIsPermissionModalOpen(true);
  };

  const handleDeleteRole = (roleId: number) => {
    const role = roles.find(r => r.id === roleId);
    if (role) {
      setRoleToDelete(role);
      setIsDeleteRoleModalOpen(true);
    }
  };

  const confirmDeleteRole = () => {
    if (roleToDelete) {
      deleteRoleMutation.mutate(roleToDelete.id);
    }
  };

  const openAssignModal = async (role: Role) => {
    setSelectedRole(role);
    setIsAssignModalOpen(true);
    
    try {
      const rolePerms = await rolesPermissionsService.rolePermissions.getRolePermissions(role.id);
      setSelectedPermissions(rolePerms.map(p => p.id));
    } catch (error) {
      console.error('Error loading role permissions:', error);
      setSelectedPermissions([]);
      errorAlert({
        title: 'Error al cargar permisos',
        message: 'No se pudieron cargar los permisos actuales del rol.',
        exception: error
      });
    }
  };

  const openAssignRolesModal = async (permission: Permission) => {
    setSelectedPermission(permission);
    setIsAssignRolesModalOpen(true);
    
    try {
      const permissionRoles = await rolesPermissionsService.rolePermissions.getPermissionRoles(permission.id);
      setSelectedRoles(permissionRoles.map(r => r.id));
    } catch (error) {
      console.error('Error loading permission roles:', error);
      setSelectedRoles([]);
      errorAlert({
        title: 'Error al cargar roles',
        message: 'No se pudieron cargar los roles actuales del permiso.',
        exception: error
      });
    }
  };

  // Permission selection handlers
  const togglePermission = (permissionId: number) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const selectAllPermissions = () => {
    const filteredIds = permissions
      .filter(permission => {
        if (!permissionSearchText) return true;
        const searchLower = permissionSearchText.toLowerCase();
        const displayName = (permission.display_name || permission.name).toLowerCase();
        const description = (permission.description || '').toLowerCase();
        const category = (permission.category || '').toLowerCase();
        const name = permission.name.toLowerCase();
        return displayName.includes(searchLower) || 
               description.includes(searchLower) || 
               category.includes(searchLower) ||
               name.includes(searchLower);
      })
      .map(p => p.id);
    setSelectedPermissions(prev => [...new Set([...prev, ...filteredIds])]);
  };

  const deselectAllPermissions = () => {
    const filteredIds = permissions
      .filter(permission => {
        if (!permissionSearchText) return true;
        const searchLower = permissionSearchText.toLowerCase();
        const displayName = (permission.display_name || permission.name).toLowerCase();
        const description = (permission.description || '').toLowerCase();
        const category = (permission.category || '').toLowerCase();
        const name = permission.name.toLowerCase();
        return displayName.includes(searchLower) || 
               description.includes(searchLower) || 
               category.includes(searchLower) ||
               name.includes(searchLower);
      })
      .map(p => p.id);
    setSelectedPermissions(prev => prev.filter(id => !filteredIds.includes(id)));
  };

  // Role selection handlers
  const toggleRole = (roleId: number) => {
    setSelectedRoles(prev => 
      prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const selectAllRoles = () => {
    setSelectedRoles(roles.map(r => r.id));
  };

  const deselectAllRoles = () => {
    setSelectedRoles([]);
  };

  const handleAssignPermissions = () => {
    if (selectedRole) {
      syncPermissionsMutation.mutate({
        roleId: selectedRole.id,
        permissionIds: selectedPermissions
      });
    }
  };

  const handleAssignRoles = () => {
    if (selectedPermission) {
      syncRolesMutation.mutate({
        permissionId: selectedPermission.id,
        roleIds: selectedRoles
      });
    }
  };

  return (
    <AppLayout title="Gestión de Roles y Permisos">
      <div className="container mx-auto p-3 sm:p-6 bg-white rounded-lg shadow">
        
        {/* Buscador principal */}
        <div className="mb-6">
          <SearchInput
            placeholder={activeTab === 'roles' ? 'Buscar roles...' : 'Buscar permisos...'}
            value={mainSearchText}
            onChange={setMainSearchText}
          />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Roles
            </TabsTrigger>
            <TabsTrigger value="permissions" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Permisos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="roles">
            <RolesList
              roles={roles}
              isLoading={rolesLoading}
              searchText={mainSearchText}
              isRoleModalOpen={isRoleModalOpen}
              onRoleModalOpenChange={setIsRoleModalOpen}
              editingRole={editingRole}
              onEditRole={handleEditRole}
              onDeleteRole={handleDeleteRole}
              onManagePermissions={openAssignModal}
              onSubmitRole={handleRoleSubmit}
              onResetRoleForm={() => setEditingRole(null)}
              isSubmitting={createRoleMutation.isPending || updateRoleMutation.isPending}
            />
          </TabsContent>

          <TabsContent value="permissions">
            <PermissionsList
              permissions={permissions}
              isLoading={permissionsLoading}
              searchText={mainSearchText}
              onEditPermission={handleEditPermission}
              onManageRoles={openAssignRolesModal}
            />
          </TabsContent>
        </Tabs>

        {/* Modales */}
        <PermissionFormModal
          isOpen={isPermissionModalOpen}
          onOpenChange={setIsPermissionModalOpen}
          editingPermission={editingPermission}
          onSubmit={handlePermissionSubmit}
          isLoading={updatePermissionMutation.isPending}
          onReset={() => setEditingPermission(null)}
        />

        <AssignPermissionsModal
          isOpen={isAssignModalOpen}
          onClose={closeAssignModal}
          selectedRole={selectedRole}
          permissions={permissions}
          selectedPermissions={selectedPermissions}
          onTogglePermission={togglePermission}
          onSelectAll={selectAllPermissions}
          onDeselectAll={deselectAllPermissions}
          onSubmit={handleAssignPermissions}
          isLoading={syncPermissionsMutation.isPending}
          searchText={permissionSearchText}
          onSearchChange={setPermissionSearchText}
        />

        <AssignRolesModal
          isOpen={isAssignRolesModalOpen}
          onClose={closeAssignRolesModal}
          selectedPermission={selectedPermission}
          roles={roles}
          selectedRoles={selectedRoles}
          onToggleRole={toggleRole}
          onSelectAll={selectAllRoles}
          onDeselectAll={deselectAllRoles}
          onSubmit={handleAssignRoles}
          isLoading={syncRolesMutation.isPending}
        />

        <DeleteRoleModal
          isOpen={isDeleteRoleModalOpen}
          onOpenChange={setIsDeleteRoleModalOpen}
          roleToDelete={roleToDelete}
          onConfirm={confirmDeleteRole}
          isLoading={deleteRoleMutation.isPending}
        />
      </div>
    </AppLayout>
  );
};

export default RolesPermissions;